import React, { useEffect, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useDispatch, useSelector } from 'react-redux';
import styles from './DeviceButtons.module.scss';

import {
  faBatteryEmpty,
  faBatteryFull,
  faBatteryHalf,
  faBatteryQuarter,
  faBatteryThreeQuarters,
  faCircleNotch,
  faGasPump,
  faToggleOff,
  faToggleOn
} from "@fortawesome/fontawesome-free-solid";
import CircularProgress from "@mui/material/CircularProgress";
import moment from "moment/moment";
import {
  always,
  cond,
  lt,
  lte,
  prop,
  T
} from 'ramda';
import GaugeChart from 'react-gauge-chart';
import { useHistory } from "react-router-dom";
import instance from "../../../../api/instance";
import { checkStatusCamera } from "../../../../api/openApi";
import LocateIcon from '../../../../assets/svg/gps_fixed_24px.svg';
import ReportsIcon from '../../../../assets/svg/history-icon.svg';
import HistoryIcon from '../../../../assets/svg/history_24dp_5F6368_FILL0_wght400_GRAD0_opsz24 1.svg';
import InfoIcon from '../../../../assets/svg/info_24dp_5F6368_FILL0_wght400_GRAD0_opsz24 1.svg';
import ViewIcon from '../../../../assets/svg/play_circle_24dp_5F6368_FILL0_wght400_GRAD0_opsz24 1.svg';
import C, { ACL, UserCan } from '../../../../shared/constants';
import * as devicedb from "../../../../shared/db/devices-db";
import { friendlySpeed } from "../../../../shared/helpers";
import { iDevicePing, iFullStoreState } from "../../../../shared/interfaces";
import { safeHas } from '../../../../shared/ramda';
import { useFirebase } from "../../../../states/firebase-state";
import { useReduxPath } from "../../../../states/redux-state";
import { iReducersState } from "../../../../stores/reducers";
import { removeReportTrips } from "../../../../stores/reducers/devicesTripsPoints/AC";
import { LOCATION_STATUS, useIsLocating } from "../../../../stores/reducers/general-reducers";
import { ReportAC } from "../../../../stores/reducers/report-reducers";
import { cameraAvailableTime, openModal, statusCameraOnline } from "../../../../stores/reducers/videoCameras";
import { getStartAndEndOfDate } from "../../../../utils";
import Captcha from "../../../Captcha/Captcha";
import Dialog, { DialogConfigSetter } from "../../../Dialog";
import { Fa } from "../../../elements/fa";
import { Col, Row } from "../../../elements/flex";
import Modal from "../../../Modal";
import Portal from "../../../Portal";
import DeviceStatuses from "../deviceStatuses";
import MuiTooltip from "@mui/material/Tooltip";

const getShadowStyle = (color: string) => ({
  boxShadow: `0px 0px 5px 1px ${color}`
});

const INTERVAL_TIME: number = 30000;

const SHADOW_COLORS = {
  status: 'rgba(0, 102, 204, 0.35)',
  view: 'rgba(0, 204, 153, 0.35)',
  history: 'rgba(255, 87, 34, 0.35)',
  locate: 'rgba(0, 204, 255, 0.35)',
  todaysHistory: 'rgba(255, 51, 51, 0.35)',
  premium: 'rgba(51, 102, 255, 0.35)',
  speed: 'rgba(255, 0, 128, 0.35)',
  fuel: 'rgba(255, 196, 0, 0.35)',
  odometer: 'rgba(255, 153, 0, 0.35)',
  volt: 'rgba(0, 255, 128, 0.35)',
  battery: 'rgba(128, 0, 255, 0.35)',
  internalBattery: 'rgba(153, 51, 255, 0.35)',
  checkEngine: 'rgba(255, 0, 0, 0.35)',
};

const DeviceButton = ({ onClick, icon, label, isLoading, shadowColor }: {
  onClick?: () => void;
  icon: string | JSX.Element;
  label: string;
  isLoading?: boolean;
  shadowColor: string;
}) => (
  <div className={styles.DeviceButton} onClick={onClick} style={getShadowStyle(shadowColor)}>
    {isLoading ? <CircularProgress size={20} /> : typeof icon === 'string' ? <img src={icon} /> : icon}
    <span>{label}</span>
  </div>
);

const SaveModeModal = ({ onCloseModal, onAcceptModal }: {
  onCloseModal: () => void;
  onAcceptModal: () => void;
}) => (
  <Portal>
    <div
      className={styles.SaveModeModalWrapper}
    >
      <div
        className={styles.SaveModeModal}
      >
        <p
          className={styles.SaveModeModalText}
        >
          This unit is asleep in power save mode. This is done to preserve
          battery life. To wake the unit up select yes. It may take up to
          2 minutes for the unit to wake up.
        </p>

        <div
          className={styles.SaveModeModalFooter}
        >
          <button
            className='but  but-white-gray but-md'
            onClick={onCloseModal}
          >
            No
          </button>
          <button
            className='but  but-orange-white but-md'
            style={{
              marginLeft: '5px',
            }}
            onClick={onAcceptModal}
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  </Portal>
);

export const DeviceButtons = ({ device, isDriver, onCheckEngineClick }) => {
  const dispatch = useDispatch();

  const [isModalShow, setIsModalShow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [availableTime, setAvailableTime] = useState<number>(null);
  const [showOfflineModal, setShowOfflineModal] = useState<boolean>(false);
  const [lastDevicePingsFuel, setLastDevicePingsFuel] = useState<number | null>(null);
  const [lastDevicePingsOdometer, setLastDevicePingsOdometer] = useState<number | null>(null);

  const { isCamera, 'extra-info': serialNumber } = device || {};
  const serial = serialNumber && Object.values(serialNumber)[0].toString();

  const { openLiveViewPlayer, openHistoryPlayer }: any = useSelector(
    (state: iReducersState) => state.videoCameras.modals
  );
  const { cameraOnline } = useSelector(
    (state: iReducersState) => state.videoCameras
  );

  const deviceLastPing = useSelector<iFullStoreState, iDevicePing | undefined | null>(
    (state) => state.devicesData.devicesLastPing.get(device.id),
    (l, r) => typeof l === typeof r && l?.pointId === r?.pointId,
  );

  const userCanDo = useSelector<iFullStoreState, Array<string>>(
    (s) => s.auth.user?.acl?.can
  );

  const CAMERA_ONLINE = cameraOnline === 1;
  const SAVE_MODE = cameraOnline === 2;

  const hasFuel = lastDevicePingsFuel;
  const hasEnergy = !!deviceLastPing?.bat_percent;
  const hasOdometer = lastDevicePingsOdometer;
  const hasVolt = !!deviceLastPing?.volt;

  const showDeviceStatus = !ACL.check(UserCan.HIDE_DEVICE_STATUS, userCanDo, true);
  const showDeviceView = !ACL.check(UserCan.HIDE_DEVICE_VIEW, userCanDo, true);
  const showDeviceHistory = !ACL.check(UserCan.HIDE_DEVICE_HISTORY, userCanDo, true);
  const showDeviceLocate = !ACL.check(UserCan.HIDE_DEVICE_LOCATE, userCanDo, true);
  const showDeviceTodaysHistory = !ACL.check(UserCan.HIDE_DEVICE_TODAYS_HISTORY, userCanDo, true);
  const showDeviceFuel = !ACL.check(UserCan.HIDE_DEVICE_FUEL, userCanDo, true);
  const showDeviceBattery = !ACL.check(UserCan.HIDE_DEVICE_BATTERY, userCanDo, true);
  const showDeviceVolt = !ACL.check(UserCan.HIDE_DEVICE_VOLT, userCanDo, true);
  const showDeviceBatteryPercent = !ACL.check(UserCan.HIDE_DEVICE_BATTERY_PERCENT, userCanDo, true);
  const showDeviceCheckEngine = !ACL.check(UserCan.HIDE_DEVICE_CHECK_ENGINE, userCanDo, true);
  const showDeviceSpeed = !ACL.check(UserCan.HIDE_DEVICE_SPEED, userCanDo, true);

  const getStatus = async (): Promise<void> => {
    setIsLoading(true);
    if (isCamera && device.id) {
      const { onlineStatus, channels, cameraTime } = await checkStatusCamera({
        firebaseDeviceId: device.id,
      });
      dispatch(statusCameraOnline(onlineStatus));
      dispatch(cameraAvailableTime(cameraTime?.available_time));
      setAvailableTime(cameraTime?.available_time);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getStatus();
    let interval;
    if (openHistoryPlayer || openLiveViewPlayer) {
      clearInterval(interval);
    } else {
      interval = setInterval(getStatus, INTERVAL_TIME);
    }

    return () => clearInterval(interval);
  }, [openHistoryPlayer, openLiveViewPlayer]);

  useEffect(() => {
    const fetchData = async () => {
      if (!deviceLastPing?.device) return;

      const [lastFuel, lastOdometer] = await Promise.all([
        devicedb.getDeviceLastFuel(deviceLastPing.device),
        devicedb.getDeviceLastOdometer(deviceLastPing.device),
      ]);

      setLastDevicePingsFuel(lastFuel);
      setLastDevicePingsOdometer(lastOdometer);
    };

    fetchData();
  }, [deviceLastPing?.device])

  const checkIfOnline = () => {
    if (!CAMERA_ONLINE) setShowOfflineModal(true);
  }

  const openCameraModal = (modalKey: string): void => {
    checkIfOnline();

    if (SAVE_MODE) {
      setIsModalShow(true);
      return;
    }

    if (CAMERA_ONLINE)
      dispatch(openModal({ key: modalKey, value: true }));

    if (CAMERA_ONLINE && availableTime === 0)
      dispatch(openModal({ key: 'openBuyMoreModal', value: true }));
  };

  const liveViewClick = (): void => {
    openCameraModal('openLiveViewModal');
  };

  const history = () => {
    openCameraModal('openHistoryModal');
  };

  const onCloseModal = () => {
    setIsModalShow(false);
  };

  const onAcceptModal = () => {
    setIsLoading(true);
    setIsModalShow(false);

    instance
      .get(`/api/device/change-cameras-state?uniqueId=${serial}&onlineState=2`)
      .then(() => {
        getStatus();
      });
  };

  const renderOfflineModal = () => {
    return (
      <Modal onBackdropClick={() => setShowOfflineModal(false)}>
        <div className={styles.offlineModalContainer}>
          <h3>Device is offline</h3>
          <p>
            This camera is currently not connected to the network. The camera may be powered off, or out of
            cellular coverage. Please wait for the camera to connect before trying to view any video.
          </p>
          <button
            onClick={() => setShowOfflineModal(false)}
            className={styles.hideOfflineModalBtn}>
            &#x2715;
          </button>
        </div>
      </Modal>
    );
  }

  const canShowFuelIndicator = showDeviceFuel && hasFuel && deviceLastPing;
  const canShowVoltIndicator = showDeviceVolt && hasVolt && safeHas('volt')(deviceLastPing);
  const canShowOdometerIndicator = deviceLastPing && hasOdometer;

  const shouldRenderTopIndicators = !!(
    canShowFuelIndicator ||
    canShowVoltIndicator ||
    canShowOdometerIndicator
  );

  return (
    <PerfectScrollbar
      className={styles.DeviceButtonsHorizontalScroll}
      options={{ useBothWheelAxes: false, suppressScrollY: true }}
    >
      <div className={styles.DeviceButtons}>
        {shouldRenderTopIndicators ? (
          <>
            {canShowFuelIndicator ? (
              <FuelIndicator percent={lastDevicePingsFuel} />
            ) : (<div className={styles.emptySpace} />)}

            {canShowVoltIndicator ? (
              <VoltageIndicator volt={prop('volt', deviceLastPing)} />
            ) : (<div className={styles.emptySpace} />)}

            {canShowOdometerIndicator ? (
              <Odometer percent={lastDevicePingsOdometer} />
            ) : (<div className={styles.emptySpace} />)}
          </>
        ) : null}

        {(showOfflineModal && !SAVE_MODE) && renderOfflineModal()}

        {isCamera && showDeviceStatus && (
          <DeviceButton
            onClick={checkIfOnline}
            icon={InfoIcon}
            label="Status"
            isLoading={isLoading}
            shadowColor={SHADOW_COLORS.status}
          />
        )}
        {isCamera && showDeviceView && (
          <DeviceButton
            onClick={liveViewClick}
            icon={ViewIcon}
            label="View"
            isLoading={isLoading}
            shadowColor={SHADOW_COLORS.view}
          />
        )}
        {isCamera && showDeviceHistory && (
          <DeviceButton
            onClick={history}
            icon={HistoryIcon}
            label="History"
            isLoading={isLoading}
            shadowColor={SHADOW_COLORS.history}
          />
        )}

        {showDeviceStatus && <DeviceStatuses device={device} />}
        {showDeviceLocate && !isDriver && <LocateDevice deviceId={device.id} />}
        {showDeviceTodaysHistory && !isDriver && <DeviceHistoryLink deviceId={device.id} />}
        {!isDriver && <GoPremiumBtn deviceId={device.id} />}

        {showDeviceBattery && hasEnergy && deviceLastPing && (
          <InternalBatteryIndicator percent={deviceLastPing?.bat_percent} />
        )}

        {showDeviceBatteryPercent && 'batteryPercent' in device && (
          <BatteryIndicator percent={device.batteryPercent} />
        )}

        {showDeviceCheckEngine && device.hasCheckEngine && (
          <CheckEngine onClick={() => onCheckEngineClick(true)} />
        )}

        {showDeviceSpeed && deviceLastPing?.speed ? (
          <div className={styles.DeviceButton} style={{ height: '59px', ...getShadowStyle(SHADOW_COLORS.speed) }}>
            <span style={{ fontSize: '14px' }}>
              {friendlySpeed(deviceLastPing.speed, device.units)}
            </span>
          </div>
        ) : null}

        {isModalShow && (
          <SaveModeModal
            onCloseModal={onCloseModal}
            onAcceptModal={onAcceptModal}
          />
        )}
      </div>
    </PerfectScrollbar>
  )
}

export const LocateDevice = React.memo(({ deviceId }: { deviceId: string }) => {
  const [remainingLocateCount] = devicedb.useMonthlyCounter('location', deviceId);
  const [locationStatus, { locate: requestLocate, clear: clearLocate }] = useIsLocating(deviceId);
  const [, setFlash] = useReduxPath((s) => s.general.flashMessage, ['general', 'flashMessage']);

  const dialogRef = React.useRef<DialogConfigSetter>();
  const setupDialog = (callBack: () => DialogConfigSetter): void => {
    dialogRef.current = callBack();
  };

  useEffect(() => {
    if (locationStatus === LOCATION_STATUS.FAIL) {
      setFlash({
        message: 'Unable to locate your device. The map will automatically update once your device is able to report its location',
        fail: true,
      });
      clearLocate();
    } else if (locationStatus === LOCATION_STATUS.SUCCESS) {
      setFlash({
        message: 'Location Successful',
        success: true,
      });
      clearLocate();
    }
  }, [locationStatus]);

  const doLocate = async () => {
    if (locationStatus !== LOCATION_STATUS.INACTIVE) {
      return;
    }

    const dialog = dialogRef.current;

    if (Number(remainingLocateCount) < 1) {
      dialog?.({
        type: 'NOTIFICATION',
        title: 'Exceeded Maximum Locates',
        body: 'Your account has exceeded the maximum locates for 24 hour period',
      });

      return;
    }

    const confirmLocation = await dialog?.({
      type: 'CONFIRM',
      title: 'Confirm Location Request',
      body: <div>
        <p>Your device automatically updates information as per your service plan. On Demand will attempt to locate your device regardless of report interval.</p>
        <p>This will deduct one On Demand Locate from your account.</p>
        <p>You have {remainingLocateCount} On Demand Locates left for this month.</p>
        <p>Click OK to locate.</p>
      </div>
    });

    if (!confirmLocation) return;

    requestLocate();
  };

  return (
    <div className={styles.DeviceButton} onClick={doLocate} style={getShadowStyle(SHADOW_COLORS.locate)}>
      <img src={LocateIcon} />
      <span>Locate</span>
      <Dialog setupConfig={setupDialog} />
    </div>
  )
});

const DeviceHistoryLink = React.memo(({ deviceId }: { deviceId: string }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const goToReports = () => {
    dispatch(removeReportTrips());
    dispatch({ type: 'REPORT_RESET_ALL_FILTERS' });

    const [startDate, endDate] = getStartAndEndOfDate();

    dispatch(ReportAC.TOGGLE_ITEM_FILTER('device', deviceId));
    dispatch(ReportAC.UPSERT_DATE_GROUP_IN_DATES({ startDate, endDate }));

    history.push('/reports');
  }

  return (
    <div className={styles.DeviceButton} onClick={() => goToReports()} style={getShadowStyle(SHADOW_COLORS.todaysHistory)}>
      <img src={ReportsIcon} />
      <span>Today’s History</span>
    </div>
  )
});

const GoPremiumBtn = ({ deviceId }: { deviceId: string }) => {
  const [premiumRequested, setPremiumRequested] = useFirebase(`devices/device-details/${deviceId}/premiumRequested`);
  const [premiumCount, setPremiumCount] = useFirebase(`devices/premium-count/${deviceId}`)
  const [remainingPremiumCount] = devicedb.useMonthlyCounter('premium', deviceId);
  const premiumUntil = useSelector<iFullStoreState, moment.Moment | undefined>((s) => s.devicesData.devicesDetails.getIn([deviceId, 'premiumUntil']) as moment.Moment | undefined);
  const canPremium = useSelector<iFullStoreState, Boolean>((s) => s.devicesData.devicesDetails.getIn([deviceId, 'canPremium']) as Boolean);
  const isPremium = premiumUntil && premiumUntil.isAfter(moment());
  const [isShowCaptcha, setIsShowCaptcha] = useState(false)

  if (isPremium && premiumRequested && premiumCount) {
    const [name, temp] = Object.entries(premiumCount)[0]
    const startOfMonth = moment().startOf('month')

    if (moment(temp.lastUpdated).isBefore(startOfMonth)) {
      temp.remaining = temp.max
    }

    temp.lastUpdated = moment().format('YYYY-MM-DDThh:mm:ss')
    temp.remaining--

    setPremiumCount({ [name]: temp })
    setPremiumRequested(false)
  }

  const dialogRef = React.useRef<DialogConfigSetter>();
  const setupDialog = (callBack: () => DialogConfigSetter) => dialogRef.current = callBack();

  const doPremiumRequest = async () => {
    if (isPremium) return;

    const dialog = dialogRef.current;

    if (!remainingPremiumCount) {
      dialog?.({
        type: 'NOTIFICATION',
        title: 'Premium unavailable',
        body: 'You have used all of your premium activations for the month',
      });

      return;
    }

    const confirmed = await dialog?.({
      type: 'CONFIRM',
      title: 'Activate Premium Service',
      body: 'You are about to activate 24 hours of premium service.',
    });

    if (!confirmed) return;

    setIsShowCaptcha(true)
  };

  if (!!canPremium) {
    return (
      <div className={styles.DeviceButton} onClick={doPremiumRequest} style={getShadowStyle(SHADOW_COLORS.premium)}>
        {isShowCaptcha && <Captcha
          onVerify={() => setPremiumRequested(true)}
          closeCaptcha={() => setIsShowCaptcha(false)} />}
        {premiumRequested && !isPremium ?
          <Fa size={15} icon={faCircleNotch} spin title="Premium upgrade is being processed" /> :
          <Fa
            size={15}
            icon={isPremium ? faToggleOn : faToggleOff}
            style={{
              fontSize: 12,
              height: 15,
              display: 'flex',
              color: isPremium ? C.primaryColor : 'initial',
              cursor: !isPremium ? 'pointer' : 'default'
            }}
          />
        }
        <span>Theft Recovery</span>
        <Dialog setupConfig={setupDialog} />
      </div>
    )
  }

  return null;
};

const Gauge = ({ percent, height, width, title, showLabel = true }) => {
  const id = title.replace(/\s+/g, '_').replace(/[^\w\-]/g, '');

  return (
    <Col
      align="center"
      style={{ zIndex: 1 }}
      justify="flex-end"
    >
      <Row justify="center" align="center" style={{ height, width }}>
        <div style={{ width: 47, marginBottom: -10 }}>
          <GaugeChart
            id={id}
            arcWidth={0.2}
            cornerRadius={1}
            hideText
            arcsLength={[0.1, 0.2, 0.7]}
            colors={['#ee3e3e', '#fddc00', '#30b22d']}
            percent={percent === 'N/A' ? 0 : percent / 100}
          />
        </div>
      </Row>
      {showLabel && <span style={{ fontSize: 9 }}>{title}: {percent === 'N/A' ? 'N/A' : `${percent}%`}</span>}
    </Col>
  )
};

const FuelIndicator = ({ percent }) => (
  <MuiTooltip title={percent === 'N/A' ? 'Fuel: N/A' : `Fuel: ${percent}%`}>
    <div className={`${styles.DeviceButton} ${styles.FuelButton}`} style={getShadowStyle(SHADOW_COLORS.fuel)}>
      <div className={styles.FuelButtonContent}>
        <Fa icon={faGasPump} className={styles.FuelIcon} />
        <Gauge height={18} width={22} percent={percent} title="Fuel" showLabel={false} />
      </div>
    </div>
  </MuiTooltip>
);

const Odometer = ({ percent }) => (
  <MuiTooltip title={`Odometer: ${Intl.NumberFormat('en-US').format(Math.trunc(percent))}`}>
    <div className={`${styles.DeviceButton} ${styles.OdometerButton}`} style={getShadowStyle(SHADOW_COLORS.odometer)}>
      <span style={{ fontSize: "15px", height: "17px" }}>
        {Intl.NumberFormat('en-US').format(Math.trunc(percent))}
      </span>
    </div>
  </MuiTooltip>
);

const VoltageIndicator = ({ volt }) => (
  <MuiTooltip title={`${Number(volt).toFixed(1)} Volts`}>
    <div className={`${styles.DeviceButton} ${styles.VoltageButton}`} style={getShadowStyle(SHADOW_COLORS.volt)}>
      <span style={{ fontSize: '15px', height: '17px' }}>
        {`${Number(volt).toFixed(1)}`}
        <span style={{ fontSize: '11px' }}>V</span>
      </span>
    </div>
  </MuiTooltip>
);

const BatteryIndicator = ({ percent }) => (
  <div className={styles.DeviceButton} style={{ height: '59px', ...getShadowStyle(SHADOW_COLORS.battery) }}>
    <Fa
      title={`${percent}%`}
      icon={cond([
        [lte(75), always(faBatteryFull)],
        [lte(50), always(faBatteryThreeQuarters)],
        [lte(25), always(faBatteryHalf)],
        [lt(0), always(faBatteryQuarter)],
        [T, always(faBatteryEmpty)],
      ])(percent)}
      style={{ color: 'orangered', fontSize: 21, height: '21px' }}
    />
    <span>Battery {percent}%</span>
  </div>
);

const InternalBatteryIndicator = ({ percent }) => (
  <MuiTooltip title={`Int. Battery: ${percent}%`}>
    <div className={`${styles.DeviceButton} ${styles.InternalBatteryButton}`} style={getShadowStyle(SHADOW_COLORS.internalBattery)}>
      <Fa
        title={`${percent}%`}
        icon={cond([
          [lte(75), always(faBatteryFull)],
          [lte(50), always(faBatteryThreeQuarters)],
          [lte(25), always(faBatteryHalf)],
          [lt(0), always(faBatteryQuarter)],
          [T, always(faBatteryEmpty)],
        ])(percent)}
        style={{ color: 'orangered', fontSize: 18, height: '18px' }}
      />
      <span>{percent}%</span>
    </div>
  </MuiTooltip>
);

const CheckEngine = ({ onClick }) => (
  <div className={styles.DeviceButton} style={{ height: '59px', ...getShadowStyle(SHADOW_COLORS.checkEngine) }} onClick={onClick}>
    <img src="images/engine.svg" alt='engine image' width={30} height={30} />
    <span title="Click to dismiss">Check Engine</span>
  </div>
);